import { isNumber } from '../type.js'

export const LRUCache = function(capacity) {
  if (!isNumber(capacity)) {
    throw new Error(`capacity ${capacity} of LRUCache is not a number`)
  }

  if (capacity < 0) {
    throw new Error(`capacity ${capacity} of LRUCache is less than 0`)
  }

  this.capacity = capacity

  this.head = {}
  this.tail = {}
  this.head.next = this.tail
  this.tail.prev = this.head

  this.nodeMap = {}
}

LRUCache.prototype = {
  has: function(key) {
    const node = this.nodeMap[key]
    return !!node
  },

  get: function(key) {
    const node = this.nodeMap[key]
    if (!node) {
      return undefined
    }
    this._moveNodeToTail(node)
    return node.value
  },

  set: function(key, value) {
    let node = this.nodeMap[key]
    if (!node) {
      node = {
        key,
        value,
      }
      this._appendNodeToTail(node)
      this.nodeMap[key] = node
    }

    if (Object.keys(this.nodeMap).length > this.capacity) {
      const removed = this._removeNode(this.head.next)
      delete this.nodeMap[removed.key]
    }
  },

  remove: function(key) {
    const node = this.nodeMap[key]
    if (!node) return

    this._removeNode(node)
    delete this.nodeMap[key]
  },

  _moveNodeToTail: function(node) {
    this._removeNode(node)
    this._appendNodeToTail(node)
  },

  _appendNodeToTail: function(node) {
    const prev = this.tail.prev
    prev.next = node
    node.prev = prev
    node.next = this.tail
    this.tail.prev = node
  },

  _removeNode: function(node) {
    const prev = node.prev
    const next = node.next

    prev.next = next
    next.prev = prev

    return node
  },
}

export const LRUExpiry = function({
  capacity,
  scanDuration = 2 * 1000,
  maxTTL = 2 * 60 * 60 * 1000,
}) {
  this.lruCache = new LRUCache(capacity)
  this.scanDuration = scanDuration

  if (this.scanDuration === 0) {
    this.scanDuration = 2 * 1000
  }

  this.startTime = new Date().getTime()
  this.maxTTL = maxTTL

  this.buckets = {}
}

LRUExpiry.prototype = {
  set: function(k, v, ttl = 5 * 60 * 1000) {
    const now = new Date().getTime()

    ttl = Math.min(ttl, this.maxTTL)

    if (ttl <= 0) {
      throw new Error(`ttl must be positive number, but get ${ttl}`)
    }

    const index = Math.floor((now + ttl - this.startTime) / this.scanDuration)
    this.buckets[index] = this.buckets[index] || []
    this.buckets[index].push(k)

    return this.lruCache.set(k, v)
  },

  has: function(key) {
    return this.lruCache.has(key)
  },

  get: function(key) {
    return this.lruCache.get(key)
  },

  remove: function(key) {
    return this.lruCache.remove(key)
  },

  _scan: function() {
    const self = this

    setTimeout(() => {
      self._scanExpire()
      self._scan()
    }, self.scanDuration)
  },

  _scanExpire: function() {
    const now = new Date().getTime()

    let index = Math.floor((now - this.startTime) / this.scanDuration) - 1

    while (index >= 0) {
      if (!this.buckets[index]) {
        break
      }

      this._expireBucket(index)

      index--
    }
  },

  _expireBucket: function(index) {
    const list = this.buckets[index]

    for (let i = 0; i < list.length; i++) {
      this.remove(list[i])
    }

    delete this.buckets[index]
  },
}
